import React, { useState } from 'react';
import { useSignIn } from '@clerk/clerk-react';
import { useHistory } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';

const ForgotPasswordPage = () => {
  const { signIn, isLoaded } = useSignIn();
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: email, 2: code & new password, 3: success

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email
      });

      setStep(2);
      setSuccess("We've sent a verification code to your email.");
    } catch (err) {
      console.error('Error requesting reset code:', err);
      setError(
        err.errors?.[0]?.message ||
          'Failed to send reset code. Please check your email.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: code,
        password: password
      });

      if (result.status === 'complete') {
        setStep(3);
        setSuccess('Your password has been reset successfully!');

        // Redirigir al login después de 3 segundos
        setTimeout(() => {
          history.push('/sign-in');
        }, 3000);
      } else {
        setError('Unable to reset password. Please try again.');
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(
        err.errors?.[0]?.message ||
          'Invalid code or password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    history.push('/sign-in');
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      {/* Logo */}
      <div className="mb-8">
        <img
          src="../assets/img/adlerlocal_logo_2026.png"
          alt="Adler Local"
          className="h-10"
        />
      </div>

      {/* Forgot Password Card */}
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          {/* Step 1: Request Code */}
          {step === 1 && (
            <>
              <div className="mb-6 text-center">
                <h1 className="text-xl font-semibold tracking-tight">
                  Forgot your password?
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Enter your email and we'll send you a code to reset your
                  password
                </p>
              </div>

              <form onSubmit={handleRequestCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="m@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                    <AlertCircle className="h-4 w-4" />
                    <p>{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full !bg-[#3766a8] hover:!bg-[#2d5286] !text-white !border-0"
                  disabled={loading}>
                  {loading ? 'Sending code...' : 'Send reset code'}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={handleBackToLogin}
                  disabled={loading}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to login
                </Button>
              </form>
            </>
          )}

          {/* Step 2: Verify Code & Set New Password */}
          {step === 2 && (
            <>
              <div className="mb-6 text-center">
                <h1 className="text-xl font-semibold tracking-tight">
                  Reset your password
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Enter the code we sent to {email} and your new password
                </p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Verification Code</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    disabled={loading}
                    maxLength={6}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    minLength={8}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading}
                    minLength={8}
                  />
                </div>

                {success && (
                  <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 dark:bg-green-900/20 p-3 rounded-md">
                    <CheckCircle className="h-4 w-4" />
                    <p>{success}</p>
                  </div>
                )}

                {error && (
                  <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                    <AlertCircle className="h-4 w-4" />
                    <p>{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full !bg-[#3766a8] hover:!bg-[#2d5286] !text-white !border-0"
                  disabled={loading}>
                  {loading ? 'Resetting password...' : 'Reset password'}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setStep(1)}
                  disabled={loading}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              </form>
            </>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <>
              <div className="mb-6 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <h1 className="text-xl font-semibold tracking-tight">
                  Password reset successfully!
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  You can now log in with your new password
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 dark:bg-green-900/20 p-3 rounded-md">
                  <CheckCircle className="h-4 w-4" />
                  <p>Redirecting to login page...</p>
                </div>

                <Button
                  type="button"
                  className="w-full !bg-[#3766a8] hover:!bg-[#2d5286] !text-white !border-0"
                  onClick={handleBackToLogin}>
                  Go to login
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
